'use strict';

/**
 * Adds a query parameter (multi-value) to the given params accumulator.
 *
 * If the key already exists in the accumulator, the value is appended.
 * Empty/undefined/null values are ignored.
 *
 * @param {Array<{key: string, value: string[]}>} params - Accumulator for query params.
 * @param {string} key - Query string key.
 * @param {string|number} value - Query string value.
 * @returns {void}
 */
function addQueryParam(params, key, value) {
    if (value === undefined || value === null || value === '') {
        return;
    }

    var existingParam = params.find(function (p) {
        return p.key === key;
    });

    if (existingParam) {
        existingParam.value.push(value);
    } else {
        params.push({ key: key, value: [value] });
    }
}

/**
 * Adds carat range params (cmin/cmax) from overrides or from the input fields.
 *
 * @param {Array<{key: string, value: string[]}>} params - Accumulator for query params.
 * @param {string|number} [cmin] - Selected minimum carat value (optional override).
 * @param {string|number} [cmax] - Selected maximum carat value (optional override).
 * @returns {void}
 */
function collectCaratParams(params, cmin, cmax) {
    var minCarat = cmin !== undefined ? cmin : $('.carat-input-min').val();
    var maxCarat = cmax !== undefined ? cmax : $('.carat-input-max').val();

    if (minCarat !== '' && maxCarat !== '') {
        // Skip default range — server applies 0/10 when cmin/cmax are absent
        var isDefaultRange = parseFloat(minCarat) === 0 && parseFloat(maxCarat) === 10;
        if (!isDefaultRange) {
            addQueryParam(params, 'cmin', minCarat);
            addQueryParam(params, 'cmax', maxCarat);
        }
    }
}

/**
 * Adds refinement checkbox/button selections to the query param accumulator.
 *
 * Expects refinement buttons to store query key/value in `data-id` and `data-value`.
 * @param {Array<{key: string, value: string[]}>} params - Accumulator for query params.
 * @returns {void}
 */
function collectCheckboxParams(params) {
    $('.refinement-value-btn.selected').each(function () {
        var key = $(this).data('id');
        var value = $(this).data('value');
        addQueryParam(params, key, value);
    });
}

/**
 * Collects all selected filters and constructs the query string
 * @param {string|number} [cmin] - Selected minimum carat value (optional override).
 * @param {string|number} [cmax] - Selected maximum carat value (optional override).
 * @returns {string} The query string
 */
function getQueryString(cmin, cmax) {
    var params = [];

    collectCheckboxParams(params);
    collectCaratParams(params, cmin, cmax);

    return params
        .map(function (p) {
            return p.key + '=' + encodeURIComponent(p.value.join(','));
        })
        .join('&');
}

/**
 * Adjusts the maximum height of the PLP (Product Listing Page) filter sidebar
 * dynamically on desktop viewports.
 *
 * - Applies only when the viewport width is greater than 1023px.
 * - Calculates the available space below the header and sticky header.
 * - Sets the `max-height` of `.refinement-bar-section` to fit within the viewport,
 *   leaving extra padding at the bottom.
 *
 * @function plpFilterMaxHeight
 * @returns {void}
 */
function plpFilterMaxHeight() {
    if ($(window).width() > 1023) {
        var itemCountTop = $('.header').height() + $('.plp-sticky-header').outerHeight() + 24;
        var filterMaxHeight = $(window).height() - itemCountTop;
        // eslint-disable-next-line quote-props
        $('.refinement-bar-section').css({ 'max-height': filterMaxHeight - 60 });
    }
}

/**
 * Carat slider values prettifier
 * @param {number} caratValue - Carat value.
 * @returns {string} Formatted value for display.
 */
function prettifyCarat(caratValue) {
    return caratValue.toFixed(2) + ' ct';
}

/**
 * ionRangeSlider onChange callback for the carat range slider.
 * Keeps the min/max input fields in sync with the slider handles.
 *
 * @param {{from: number, to: number}} data - Slider state provided by ionRangeSlider.
 * @returns {void}
 */
function caratOnChange(data) {
    $('.carat-input-min').val(data.from.toFixed(2));
    $('.carat-input-max').val(data.to.toFixed(2));
}

/**
 * Updates the product grid via AJAX
 * @param {string} url - The URL to fetch.
 * @returns {void}
 */
function updateGrid(url) {
    $.spinner().start();
    $.ajax({
        url: url,
        method: 'GET',
        success: function (data) {
            if (!data || data.trim() === '') {
                $.spinner().stop();
                return;
            }
            $('.custom-plp-search-results').empty().html(data);
            $('.modal-background').addClass('d-none');
            $('body').removeClass('overflow-hidden');
            $('body').trigger('customplp:gridUpdated');
            $.spinner().stop();
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * Applies the carat range filter by updating the URL (history) and fetching the new grid.
 * @param {number|string} cmin - Selected minimum carat.
 * @param {number|string} cmax - Selected maximum carat.
 * @returns {void}
 */
function applyRangeFilter(cmin, cmax) {
    var queryString = getQueryString(cmin, cmax);
    var url = 'CustomPLP-UpdateGrid?' + queryString;
    var pageUrl = 'CustomPLP-Show?' + queryString;

    window.history.pushState({}, '', pageUrl);
    updateGrid(url);
}

/**
 * Initializes the carat range slider (ionRangeSlider) if present.
 *
 * Reads `min`, `max`, `from`, `to` from `.js-carat-range-slider` data attributes,
 * syncs the input fields on change, and triggers an AJAX refresh on finish.
 * @returns {void}
 */
function caratFilterSlider() {
    const $caratRangeSlider = $('.js-carat-range-slider');

    if (!$caratRangeSlider.length) {
        return;
    }

    // Read values from ISML data attributes
    const minCarat = parseFloat($caratRangeSlider.data('min'));
    const maxCarat = parseFloat($caratRangeSlider.data('max'));
    const fromCarat = parseFloat($caratRangeSlider.data('from'));
    const toCarat = parseFloat($caratRangeSlider.data('to'));

    $caratRangeSlider.ionRangeSlider({
        skin: 'round',
        type: 'double',
        min: minCarat,
        max: maxCarat,
        from: fromCarat,
        to: toCarat,
        step: 0.01,
        hide_min_max: true,
        extra_classes: 'ion-caratfilter-slider',
        prettify: prettifyCarat,
        onChange: caratOnChange,
        onFinish: function (data) {
            if (!(data.from === this.from && data.to === this.to)) {
                caratOnChange(data);
                applyRangeFilter(data.from.toFixed(2), data.to.toFixed(2));
            }
        },
        min_interval: 0.01
    });
}

$(document).ready(function () {
    // Intercept handleFilters for CustomPLP before search.js handles it
    $('body').on('handleFilters', function (e, data) {
        if (!$('.custom-plp-search-results').length) { return; }

        var url = data && data.context && data.context.data('href');
        if (!url || url.indexOf('CustomPLP') === -1) { return; }

        e.stopImmediatePropagation();

        // Preserve carat values when applying filters via handleFilters event
        var minVal = $.trim($('.carat-input-min').val());
        var maxVal = $.trim($('.carat-input-max').val());
        var cmin = minVal ? parseFloat(minVal) : null;
        var cmax = maxVal ? parseFloat(maxVal) : null;

        var gridUrl = url.replace('CustomPLP-Show', 'CustomPLP-UpdateGrid');
        var pageUrl = url.indexOf('CustomPLP-Show') > -1 ? url : url.replace('CustomPLP-UpdateGrid', 'CustomPLP-Show');

        // Add carat params if values are valid and not default
        if (!Number.isNaN(cmin) && !Number.isNaN(cmax) && minVal !== '' && maxVal !== '') {
            var isDefaultRange = cmin === 0 && cmax === 10;
            if (!isDefaultRange) {
                var paramSeparator = url.indexOf('?') > -1 ? '&' : '?';
                gridUrl += paramSeparator + 'cmin=' + cmin.toFixed(2) + '&cmax=' + cmax.toFixed(2);
                pageUrl += paramSeparator + 'cmin=' + cmin.toFixed(2) + '&cmax=' + cmax.toFixed(2);
            }
        }

        window.history.pushState({}, '', pageUrl);
        updateGrid(gridUrl);
    });

    $('body').on('customplp:gridUpdated', function () {
        plpFilterMaxHeight();
        caratFilterSlider();
    });

    plpFilterMaxHeight();
    caratFilterSlider();

    $(window).resize(function () {
        plpFilterMaxHeight();
    });

    // CustomPLP only — standard PLP shape tiles are handled by applyFilter in search.js
    $('body').on('click', '.refinement-value-btn', function (e) {
        if (!$('.custom-plp-search-results').length) { return; }
        e.preventDefault();
        $(this).toggleClass('selected');

        // Preserve carat values when applying other filters
        var cmin = parseFloat($('.carat-input-min').val());
        var cmax = parseFloat($('.carat-input-max').val());
        var queryString = getQueryString(cmin, cmax);
        var url = $('#search-results-url').val() + '?' + queryString;
        var pageUrl = $('#search-page-url').val() + '?' + queryString;

        window.history.pushState({}, '', pageUrl);
        updateGrid(url);
    });

    // Apply Range Filter
    $('body').on('click', '.apply-range-btn', function (e) {
        e.preventDefault();
        const cmin = parseFloat($('.carat-input-min').val());
        const cmax = parseFloat($('.carat-input-max').val());
        applyRangeFilter(cmin, cmax);
    });

    $('body').on('input', '.carat-input', function () {
        let value = $(this).val();

        // Allow digits and one decimal point
        value = value.replace(/[^0-9.]/g, '');

        if ((value.match(/\./g) || []).length > 1) {
            value = value.slice(0, -1);
        }

        $(this).val(value);
    });

    $('body').on('change', '.carat-input', function () {
        const $slider = $('.js-carat-range-slider');
        const sliderInstance = $slider.data('ionRangeSlider');

        let enteredMin = parseFloat($('.carat-input-min').val());
        let enteredMax = parseFloat($('.carat-input-max').val());

        const minCarat = parseFloat($slider.data('min'));
        const maxCarat = parseFloat($slider.data('max'));

        // Defaults if invalid
        if (Number.isNaN(enteredMin)) {
            enteredMin = minCarat;
        }
        if (Number.isNaN(enteredMax)) {
            enteredMax = maxCarat;
        }

        // Range validation
        if (enteredMin >= enteredMax) {
            enteredMin = minCarat;
            enteredMax = maxCarat;
        } else if (enteredMin < minCarat) {
            enteredMin = minCarat;
        } else if (enteredMax > maxCarat) {
            enteredMax = maxCarat;
        }

        // Update slider handles
        if (sliderInstance) {
            sliderInstance.update({
                from: enteredMin,
                to: enteredMax
            });
        }

        applyRangeFilter(enteredMin, enteredMax);
    });

    $('body').on('click', '.custom-reset', function (e) {
        e.preventDefault();
        var url = $(this).data('href');
        var pageUrl = $(this).data('page-url');
        window.history.pushState({}, '', pageUrl);
        updateGrid(url);
    });

    /**
     * Updates CustomPLP result counts to match the number of rendered tiles.
     */
    function updateDiamondProductCount() {
        var visibleProductCount = $('.product-grid .tile-space').length;

        if (!visibleProductCount) {
            return;
        }

        $('[data-count-template]').each(function () {
            var $productCount = $(this);
            var countTemplate = $productCount.data('countTemplate');

            if (countTemplate && countTemplate.indexOf('count') > -1) {
                $productCount.text(countTemplate.replace('count', visibleProductCount));
            }
        });
    }

    // Filter chip removal — body-level to survive DOM replacement by updateGrid
    $('body').on('click', '.filter-bar .selected-filter', function (e) {
        e.preventDefault();
        var url = $(this).data('href');

        if (!url) { return; }
        var queryString = url.split('?')[1] || '';

        var gridUrl = $('#search-results-url').val() + '?' + queryString;
        var pageUrl = url.indexOf('CustomPLP-Show') > -1
            ? url
            : url.replace('CustomPLP-UpdateGrid', 'CustomPLP-Show');

        window.history.pushState({}, '', pageUrl);
        updateGrid(gridUrl);
    });

    $(document).on('click', '.show-more-doc .seemore-button', function (e) {
        e.stopPropagation();
        var showMoreUrl = $(this).data('url');
        e.preventDefault();

        $.spinner().start();
        $.ajax({
            url: showMoreUrl,
            data: { selectedUrl: showMoreUrl },
            method: 'GET',
            success: function (response) {
                $('.grid-footer').replaceWith(response);
                $('body').trigger('search:updateDom', '.grid-footer');
                updateDiamondProductCount();
                $.spinner().stop();
            },
            error: function () {
                $.spinner().stop();
            }
        });
    });
});
